/**
 * The voice-over's script, editable any time after it was made: type it, or tell Lykke how to rewrite it; change the
 * voice or the pace; "Update the voice" re-makes the take from the script (same start, music dips follow).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import type { VoicePart } from '@/video/spec';

type Pace = 'calm' | 'normal' | 'fast';
const WPS: Record<Pace, number> = { calm: 2.2, normal: 2.5, fast: 2.9 };

export function VoiceoverPanel({ videoId, part, room, label = 'Script', onRemade }: {
  videoId: string;
  /** The clip being edited (a whole take, or one scene's line). */
  part: VoicePart;
  label?: string;
  /** Seconds from the voice-over's start to the end of the video. */
  room: number;
  onRemade: (take: { url: string; seconds: number; script: string; voice: string; pace: Pace }) => void;
}) {
  const [script, setScript] = useState(part.text || '');
  const [voice, setVoice] = useState(part.voice || 'Aria');
  const [pace, setPace] = useState<Pace>(part.pace || 'normal');
  const [instruction, setInstruction] = useState('');
  const [writing, setWriting] = useState(false);
  const [making, setMaking] = useState(false);
  // A new take arrived (undo, another tab, Lykke): show it.
  useEffect(() => { setScript(part.text || ''); setVoice(part.voice || 'Aria'); setPace(part.pace || 'normal'); }, [part.url, part.text, part.voice, part.pace]);
  const voices = useQuery({ queryKey: ['video-voices'], queryFn: () => coworkApi.videos.voices(), staleTime: Infinity });
  const words = script.split(/\s+/).filter(Boolean).length;
  const fits = words <= Math.ceil(room * WPS[pace]) + 1;
  const changed = script.trim() !== (part.text || '').trim() || voice !== (part.voice || 'Aria') || pace !== (part.pace || 'normal');

  async function rewrite() {
    setWriting(true);
    try {
      const how = instruction.trim();
      const r = await coworkApi.videos.voiceoverScript(videoId, { instruction: how ? `${how}. The current script is: "${script.trim()}"` : script.trim() ? `Improve this script, same meaning: "${script.trim()}"` : undefined, pace, seconds: room });
      setScript(r.script); setInstruction('');
    } catch (e) { toast.error((e as Error).message); }
    finally { setWriting(false); }
  }
  async function remake() {
    if (!script.trim()) return;
    setMaking(true);
    try {
      // It must fit its slot (to the next line, or the end of the video): fitted on arrival when it reads longer.
      const take = await coworkApi.videos.voiceover(videoId, { script: script.trim(), voice, pace, target: Math.max(0.5, room - 0.05) });
      onRemade({ ...take, script: script.trim(), voice, pace });
      toast.success(take.fitted ? `Voice updated — read in ${take.fitted.from.toFixed(1)} s, fitted to ${take.fitted.to.toFixed(1)} s` : take.over ? `Voice updated — it runs ${take.over.toFixed(1)} s long; shorten the line a little` : `Voice updated (${take.seconds.toFixed(1)} s)`);
    }
    catch (e) { toast.error((e as Error).message); }
    finally { setMaking(false); }
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-[#eaecf0] p-3">
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] font-semibold text-[#344054]">{label}</span>
        <span className={`text-[11px] tabular-nums ${fits ? 'text-[#667085]' : 'text-[#b54708]'}`}>{words} words · ~{(words / WPS[pace]).toFixed(1)} s of {room.toFixed(1)} s</span>
      </div>
      <div className="relative">
        <textarea value={script} onChange={(e) => setScript(e.target.value)} rows={5} maxLength={1200} className="w-full rounded-lg border border-[#d0d5dd] px-2.5 py-2 text-[13px] leading-relaxed text-[#101828] focus:border-[#be43ff] focus:outline-none focus:ring-2 focus:ring-[#f4ebff]" />
        {writing && <span className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-white/80 text-[12.5px] text-[#6941c6]"><Loader2 className="h-4 w-4 animate-spin" />Lykke is rewriting…</span>}
      </div>
      {!fits && <p className="text-[11.5px] text-[#b54708]">Too long for the time left — cut words, pick a faster pace, or move the voice-over earlier.</p>}
      <div className="flex gap-1.5">
        <input value={instruction} onChange={(e) => setInstruction(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void rewrite(); }} placeholder="Tell Lykke: shorter, warmer, add the offer…" className="h-8 min-w-0 flex-1 rounded-lg border border-[#d0d5dd] px-2.5 text-[12.5px] focus:border-[#be43ff] focus:outline-none" />
        <button type="button" disabled={writing} onClick={() => void rewrite()} className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[#d0d5dd] px-2.5 text-[12px] font-semibold text-[#344054] hover:bg-[#f9fafb] disabled:opacity-40"><Wand2 className="h-3.5 w-3.5 text-[#7a1fbf]" />Rewrite</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1"><span className="text-[11.5px] font-semibold text-[#475467]">Voice</span>
          <select value={voice} onChange={(e) => setVoice(e.target.value)} className="rounded-lg border border-[#d0d5dd] px-2 py-1.5 text-[12.5px]">
            {(voices.data?.voices || [{ id: voice, label: voice, line: '' }]).map((v) => <option key={v.id} value={v.id}>{v.label}{v.line ? ` — ${v.line}` : ''}</option>)}
          </select></label>
        <label className="flex flex-col gap-1"><span className="text-[11.5px] font-semibold text-[#475467]">Pace</span>
          <select value={pace} onChange={(e) => setPace(e.target.value as Pace)} className="rounded-lg border border-[#d0d5dd] px-2 py-1.5 text-[12.5px]">
            <option value="calm">Calm</option><option value="normal">Normal</option><option value="fast">Fast</option>
          </select></label>
      </div>
      <button type="button" disabled={!changed || !script.trim() || making || writing} onClick={() => void remake()}
        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#2e024c] px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-40">
        {making ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 text-[#a9f600]" />}{making ? 'Making the voice…' : changed ? 'Update the voice' : 'Up to date'}
      </button>
    </div>
  );
}
